// Sprint C PR (an): wires the Settings → Spaces section to the real
// Stream-2 `GET /api/spaces` endpoint.
//
// "Space" here = admin-owned namespace (the HYBRID-renamed concept). It is
// distinct from the "room" (member-of) concept that powers the sidebar.
// See `SPACES_BACKEND_WIRE.md` for the wire contract.

import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSpace, deleteSpace, updateSpace, fetchSpaces } from '../lib/api';
import type { ApiError, Space, SpaceState, UpdateSpaceRequest } from '../lib/api';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { ScheduleMetaAffix, SpaceSchedules } from './SpaceSchedules';

const STATE_LABEL: Record<SpaceState, string> = {
  active: 'Active',
  sleeping: 'Sleeping',
  'pending-deletion': 'Pending deletion',
};

// One cadence, two registers. `label` is what the <select> says, where there is
// room to read a sentence; `short` is what the dense `·`-joined meta-line says,
// beside "↻ Daily 8:00". Both come from here, so they cannot drift apart.
const CHECK_IN_OPTIONS = [
  { value: 15, label: 'Every 15 minutes', short: 'every 15m' },
  { value: 30, label: 'Every 30 minutes', short: 'every 30m' },
  { value: 60, label: 'Hourly', short: 'hourly' },
  { value: 240, label: 'Every 4 hours', short: 'every 4h' },
  { value: 1440, label: 'Daily', short: 'daily' },
] as const;

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * The meta-line's half of the cadence story: "Check-in **every 4h**", which sits
 * beside "↻ Daily 8:00". Compact on purpose — the meta-line is a dense `·`-joined
 * strip, not a sentence, and at 195px the drawer has no room for "Every 4 hours".
 *
 * Every offered cadence takes its wording from `CHECK_IN_OPTIONS`, so the meta-line
 * and the `<select>` can never name the same setting differently. Only a value the
 * dropdown does not offer (set by an older client, or by the API) falls through to
 * the generic form.
 */
function formatCheckIn(minutes: number | null): string {
  if (!minutes) return 'not set';
  const option = CHECK_IN_OPTIONS.find((o) => o.value === minutes);
  if (option) return option.short;
  if (minutes % 1440 === 0) return `every ${minutes / 1440}d`;
  if (minutes % 60 === 0) return `every ${minutes / 60}h`;
  return `every ${minutes}m`;
}

export function SpacesSection() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['spaces'],
    queryFn: fetchSpaces,
  });

  const [loadError, setLoadError] = useState<ApiError | Error | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [justCreatedName, setJustCreatedName] = useState<string | null>(null);

  const createTriggerRef = useRef<HTMLButtonElement>(null);
  const createNameRef = useRef<HTMLInputElement>(null);
  const wasCreating = useRef(false);

  // Focus order: when the create form opens, move focus into the Name field;
  // when it closes, return focus to the trigger. Guarded so the first render
  // doesn't steal focus.
  useEffect(() => {
    if (showCreate) {
      createNameRef.current?.focus();
    } else if (wasCreating.current) {
      createTriggerRef.current?.focus();
    }
    wasCreating.current = showCreate;
  }, [showCreate]);

  useEffect(() => {
    if (isError && error) {
      setLoadError(error as unknown as ApiError | Error);
    } else if (data) {
      setLoadError(null);
    }
  }, [data, error, isError]);

  const createMutation = useMutation({
    mutationFn: createSpace,
    onSuccess: (_result, variables) => {
      // Refresh BOTH lists: the Settings list (['spaces']) AND the sidebar
      // (['me','spaces']) — the sidebar is where messaging happens, so without
      // this the create flow dead-ends with no path to open/message the new
      // space (#1216).
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'spaces'] });
      setJustCreatedName(variables.name);
      setShowCreate(false);
      setName('');
      setDescription('');
    },
  });

  function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
    });
  }

  function handleCancelCreate() {
    setShowCreate(false);
    setName('');
    setDescription('');
    createMutation.reset();
  }

  if ((isError || loadError) && !data) {
    const apiError = (loadError ?? error) as unknown as ApiError | undefined;
    const isAuthError = apiError?.status === 401;
    return (
      <div className="settings-section">
        <h3>Spaces</h3>
        <div className="login-form-error settings-section-retry-error" role="alert">
          <p className="settings-section-error-message">
            {isAuthError
              ? 'Sign in to view your spaces.'
              : "We couldn't load your spaces."}
          </p>
          {!isAuthError && (
            <button
              type="button"
              className="space-view-error-retry"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>Spaces</h3>
        <Skeleton
          className="settings-section-loading"
          ariaLabel="Loading spaces"
          rows={3}
          variant="list"
        />
      </div>
    );
  }

  const spaces = data?.spaces ?? [];
  const createApiErr = createMutation.error as ApiError | null;

  return (
    <div className="settings-section">
      <h3>Spaces</h3>

      {justCreatedName && (
        <div className="spaces-create-success" role="status" aria-live="polite">
          <p className="spaces-create-success-text">
            <strong>“{justCreatedName}”</strong> created. It now appears in the
            sidebar under <strong>SPACES</strong> — open it there to read and
            send messages.
          </p>
          <button
            type="button"
            className="settings-action-secondary"
            onClick={() => setJustCreatedName(null)}
          >
            Done
          </button>
        </div>
      )}

      {!showCreate ? (
        <button
          type="button"
          ref={createTriggerRef}
          className="settings-action-primary"
          onClick={() => {
            setJustCreatedName(null);
            setShowCreate(true);
          }}
        >
          {spaces.length === 0 ? 'Create your first space' : 'Create Space'}
        </button>
      ) : (
        <form
          className="spaces-create-form"
          onSubmit={handleCreate}
          aria-label="Create space"
        >
          <div className="spaces-create-form-fields">
            <label htmlFor="space-name" className="spaces-create-label">
              Name
            </label>
            <input
              id="space-name"
              ref={createNameRef}
              type="text"
              className="spaces-create-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={createMutation.isPending}
              required
              placeholder="my-space"
              aria-required="true"
            />
            <label htmlFor="space-description" className="spaces-create-label">
              Description{' '}
              <span className="spaces-create-optional">(optional)</span>
            </label>
            <input
              id="space-description"
              type="text"
              className="spaces-create-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={createMutation.isPending}
              placeholder="What is this space for?"
            />
          </div>
          {createApiErr && (
            <p className="settings-error" role="alert">
              {createApiErr.message}
            </p>
          )}
          <div className="settings-actions">
            <button
              type="submit"
              className="settings-action-primary"
              disabled={!name.trim() || createMutation.isPending}
            >
              {createMutation.isPending && (
                <span className="button-spinner" aria-hidden="true" />
              )}
              {createMutation.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              className="settings-action-secondary"
              onClick={handleCancelCreate}
              disabled={createMutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {spaces.length === 0 && (
        <EmptyState
          className="settings-empty-state spaces-empty-state"
          icon="🛠️"
          title="No spaces yet"
          description="Create one above or from the CLI to start a new admin namespace."
          action={
            <code className="settings-cli-hint-code">
              tizok space create &lt;name&gt;
            </code>
          }
        />
      )}

      {spaces.length > 0 && (
        <>
          <p className="settings-section-help">
            Spaces you own. Each also appears in the sidebar’s{' '}
            <strong>SPACES</strong> list, where you open it to read and send
            messages. Create with{' '}
            <code className="settings-cli-hint-code">tizok space create</code>;
            archive with{' '}
            <code className="settings-cli-hint-code">tizok space archive</code>.
          </p>
          <ul className="spaces-list" aria-label="Spaces">
            {spaces.map((space) => (
              <SpaceRow key={space.space_id} space={space} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function SpaceRow({ space }: { space: Space }) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(space.name);
  const [editDescription, setEditDescription] = useState(
    space.description ?? '',
  );
  const [editCheckInMinutes, setEditCheckInMinutes] = useState(
    String(space.check_in_minutes ?? 240),
  );

  const editTriggerRef = useRef<HTMLButtonElement>(null);
  const editNameRef = useRef<HTMLInputElement>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  const wasConfirming = useRef(false);

  // Focus order: entering edit mode moves focus to the Name field; leaving it
  // returns focus to the Edit button. Guarded against initial mount.
  useEffect(() => {
    if (editing) {
      editNameRef.current?.focus();
    } else if (wasEditing.current) {
      editTriggerRef.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  // Opening the delete confirmation moves focus to Confirm (so the group's
  // accessible name is announced); cancelling returns focus to Delete.
  useEffect(() => {
    if (confirmDelete) {
      confirmRef.current?.focus();
    } else if (wasConfirming.current) {
      deleteTriggerRef.current?.focus();
    }
    wasConfirming.current = confirmDelete;
  }, [confirmDelete]);

  const deleteMutation = useMutation({
    mutationFn: () => deleteSpace(space.space_id),
    onSuccess: () => {
      setConfirmDelete(false);
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateSpaceRequest) => updateSpace(space.space_id, body),
    onSuccess: () => {
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['spaces'] });
    },
  });

  function handleEditSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const updates: UpdateSpaceRequest = {};
    const selectedCheckInMinutes = Number(editCheckInMinutes);
    if (editName.trim() !== space.name) updates.name = editName.trim();
    if (editDescription.trim() !== (space.description ?? ''))
      updates.description = editDescription.trim();
    if (selectedCheckInMinutes !== (space.check_in_minutes ?? 240))
      updates.check_in_minutes = selectedCheckInMinutes;
    if (Object.keys(updates).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(updates);
  }

  function handleCancelEdit() {
    setEditing(false);
    setEditName(space.name);
    setEditDescription(space.description ?? '');
    setEditCheckInMinutes(String(space.check_in_minutes ?? 240));
    updateMutation.reset();
  }

  const stateClass = `spaces-row-state spaces-row-state-${space.state}`;
  const deleteApiErr = deleteMutation.error as ApiError | null;
  const updateApiErr = updateMutation.error as ApiError | null;

  if (editing) {
    return (
      <li className="spaces-row">
        <form
          className="spaces-edit-form"
          onSubmit={handleEditSubmit}
          aria-label={`Edit ${space.name}`}
        >
          <label htmlFor={`edit-name-${space.space_id}`} className="spaces-create-label">
            Name
          </label>
          <input
            id={`edit-name-${space.space_id}`}
            ref={editNameRef}
            type="text"
            className="spaces-create-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={updateMutation.isPending}
            required
          />
          <label htmlFor={`edit-desc-${space.space_id}`} className="spaces-create-label">
            Description
          </label>
          <input
            id={`edit-desc-${space.space_id}`}
            type="text"
            className="spaces-create-input"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            disabled={updateMutation.isPending}
          />
          <label
            htmlFor={`edit-check-in-${space.space_id}`}
            className="spaces-create-label"
          >
            Managed check-in
          </label>
          <select
            id={`edit-check-in-${space.space_id}`}
            className="spaces-create-input"
            value={editCheckInMinutes}
            onChange={(e) => setEditCheckInMinutes(e.target.value)}
            disabled={updateMutation.isPending}
          >
            {CHECK_IN_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {updateApiErr && (
            <p className="settings-error" role="alert">
              {updateApiErr.message}
            </p>
          )}
          <div className="settings-actions">
            <button
              type="submit"
              className="settings-action-primary"
              disabled={!editName.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="settings-action-secondary"
              onClick={handleCancelEdit}
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="spaces-row">
      <div className="spaces-row-main">
        <div className="spaces-row-name">{space.name}</div>
        {space.description && (
          <div className="spaces-row-description">{space.description}</div>
        )}
        <div className="spaces-row-meta">
          <span className={stateClass}>{STATE_LABEL[space.state]}</span>
          <span className="spaces-row-meta-sep" aria-hidden="true">
            ·
          </span>
          <span>Last activity {formatTimestamp(space.last_activity_at)}</span>
          <span className="spaces-row-meta-sep" aria-hidden="true">
            ·
          </span>
          <span>Check-in {formatCheckIn(space.check_in_minutes)}</span>
          {/* One meta-line, two cadences: the sleep timeout and the Requests this
              space sends itself. "Check-in every 4h · ↻ Daily 8:00". */}
          <ScheduleMetaAffix spaceId={space.space_id} />
          {space.scheduled_purge_at && (
            <>
              <span className="spaces-row-meta-sep" aria-hidden="true">
                ·
              </span>
              <span>Purge {formatTimestamp(space.scheduled_purge_at)}</span>
            </>
          )}
        </div>
        {/* Armed from the composer's ▾, never from here. `state` lets a row rest
            honestly ("asleep · wakes 8:00") instead of claiming a pulse. */}
        <SpaceSchedules spaceId={space.space_id} spaceState={space.state} />
      </div>
      <div className="spaces-row-actions">
        <button
          type="button"
          ref={editTriggerRef}
          className="spaces-row-edit-btn"
          onClick={() => {
            setEditName(space.name);
            setEditDescription(space.description ?? '');
            setEditCheckInMinutes(String(space.check_in_minutes ?? 240));
            setEditing(true);
          }}
          aria-label={`Edit ${space.name}`}
        >
          Edit
        </button>
        {!confirmDelete ? (
          <button
            type="button"
            ref={deleteTriggerRef}
            className="spaces-row-delete-btn"
            onClick={() => setConfirmDelete(true)}
            disabled={deleteMutation.isPending}
            aria-label={`Delete ${space.name}`}
          >
            Delete
          </button>
        ) : (
          <div
            className="spaces-row-confirm"
            role="group"
            aria-label={`Confirm delete ${space.name}`}
          >
            <span className="spaces-row-confirm-text">
              Are you sure? This is a 30-day soft-delete.
            </span>
            {deleteApiErr && (
              <span className="settings-error" role="alert">
                {deleteApiErr.message}
              </span>
            )}
            <button
              type="button"
              ref={confirmRef}
              className="spaces-row-confirm-btn"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
            </button>
            <button
              type="button"
              className="spaces-row-cancel-btn"
              onClick={() => {
                setConfirmDelete(false);
                deleteMutation.reset();
              }}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
